import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { object, string, boolean } from 'yup';
import { uniqBy } from 'lodash';
import { useFormik } from 'formik';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import { DATA_RETRIEVAL_INTERVAL, TEXT_LENGTH_LIMITS } from '../../constants/common';

// Components
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../components/InputControl/InputControl';
import SelectGroupOne from '../../components/Forms/SelectGroup/SelectGroupOne';

import DatePickerOne from '../../components/Forms/DatePicker/DatePickerOne';
import SwitcherOne from '../../components/Switchers/SwitcherOne';
import Switcher, { ResponseSwitch } from '../../components/Switcher/Switcher';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';
import { DefaultMetaFieldsWrapper } from '../../components/DefaultMetaFields';

const NO_SELECTED_LOCATIONS = 'Client not selected';

type EditContractProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

type FormValues = {
  name: string | null;
  start_at: Date | null;
  end_at: Date | null;
  locationId: string | null;
  endless: boolean | null;
  isCelsius: boolean;
};

const EditContract: React.FC<EditContractProps> = ({
  contract,
  getContract,
  updateContract,
  locations,
  getLocations,
  clearContractState,
  organization,
  getOrganization,
}) => {
  const { id } = useParams();

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [options, setOptions] = useState(null);
  const [initialIsCelsius, setInitialIsCelsius] = useState(false);

  const formik = useFormik<FormValues>({
    initialValues: {
      name: null,
      start_at: null,
      end_at: null,
      locationId: null,
      endless: null,
      isCelsius: false,
    },
    validationSchema: object({
      name: string()
        .required('Required')
        .min(
          TEXT_LENGTH_LIMITS.MIN,
          `Min name length is ${TEXT_LENGTH_LIMITS.MIN} characters`
        )
        .max(
          TEXT_LENGTH_LIMITS.MAX,
          `Max  name length is ${TEXT_LENGTH_LIMITS.MAX} characters`
        ),
      is_active: boolean(),
      start_at: string()
        .required('Required')
        .transform((value) => (value === '' ? null : value))
        .typeError('Must be a valid timestamp')
        .test(
          'date_must_be_now_or_earlier',
          'Start date must be the current time or earlier',
          function (value) {
            if (!value) return true;

            const date = new Date(value);
            const now = new Date();

            return date < now;
          }
        ),
      end_at: string()
        .nullable()
        .transform(value => (value === '' ? null : value))
        .when('endless', {
          is: false,
          then: (schema) =>
            schema
              .required('End date is required when contract is not endless')
              .test(
                'is-at-least-1-hour-after-start',
                'End date must be at least 1 hour after start date',
                function (endValue) {
                  const { start_at } = this.parent;

                  if (!endValue || !start_at) return true;

                  const end = new Date(endValue).getTime();
                  const start = new Date(start_at).getTime();

                  return end >= start + DATA_RETRIEVAL_INTERVAL.ONE_HOUR;
                }
              ),
          otherwise: (schema) => schema.nullable(),
        })
    }),
    onSubmit: async (values) => {
      const { name, start_at, end_at, locationId, endless, isCelsius } = values;
      const formData: any = {
        name,
        start_at,
        end_at,
        locationId,
        endless,
      };
      if (isCelsius !== initialIsCelsius) {
        formData.isCelsius = isCelsius;
      }
      await updateContract({
        id,
        formData,
      });
    },
  });

  const disabledSubmit = Object.keys(formik.errors).length > 0 || Object.keys(formik.touched).length === 0;

  const handleSelect = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;

    formik.handleChange({
      target: { name, value },
    });
    await formik.setFieldTouched(name, true);
  };

  const onUnitsChange = (data: ResponseSwitch) => {
    formik.setFieldValue('isCelsius', data.value);
    formik.setFieldTouched('isCelsius', true);
  };

  const fetchData = async (id) => {
    await Promise.all([getContract(id), getLocations(), getOrganization()]);
    setIsDataLoaded(true);
  };

  const generateContractOptions = () => {
    Object.entries(contract).forEach(([key, value]) => {
      formik.handleChange({
        target: {
          name: key,
          value: value,
        },
      });
    });

    let isCelsiusValue = organization?.isCelsius ?? false;
    if (contract.isCelsius != null) {
      isCelsiusValue = contract.isCelsius;
    } else if (contract.locationId && locations) {
      const selectedLocation = locations.find((loc) => loc.id === contract.locationId);
      if (selectedLocation) {
        isCelsiusValue = selectedLocation.isCelsius;
      }
    }
    formik.setFieldValue('isCelsius', isCelsiusValue);
    setInitialIsCelsius(isCelsiusValue);

    const optionsByLocation =
      locations?.map((location) => {
        return ({
          value: location.id,
          label: location.name,
          disabled: false,
        })
      }) || [];

    const options = [...optionsByLocation];

    options.push({
      value: '',
      label: NO_SELECTED_LOCATIONS,
      disabled: false,
    });

    setOptions(uniqBy(options, 'value'));
  };

  useEffect(() => {
    if (!contract) return;
    generateContractOptions();
  }, [contract, locations, organization]);

  useEffect(() => {
    if (contract && formik.values.locationId && locations && contract.isCelsius == null) {
      const selectedLocation = locations.find((loc) => loc.id === formik.values.locationId);
      if (selectedLocation) {
        formik.setFieldValue('isCelsius', selectedLocation.isCelsius);
      }
    }
  }, [contract, formik.values.locationId, locations]);

  useEffect(() => {
    setIsDataLoaded(false);
    fetchData(id);

    return () => {
      clearContractState();
    };
  }, [id]);

  const handleDateChange = async (name, value) => {
    await formik.setFieldValue(name, value);
    await formik.setFieldTouched(name, true);
  };

  const handleSwitcherChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    formik.handleChange(event);

    if (!formik.touched[name]) {
      await formik.setFieldTouched(name, true);
    }

    if (checked) {
      const filteredErrors = Object.fromEntries(
        Object.entries(formik.errors).filter(([key]) => key !== 'end_at')
      );
      formik.setErrors(filteredErrors);
    } else {
      await formik.validateField('end_at');
    }
  };

  return (
    <>
      {!isDataLoaded && (
        <div className="flex items-center justify-center">Loading...</div>
      )}
      {isDataLoaded && (
        <div className="p-edit-contracts pb-4 md:pb-25">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
              <h3 className="font-medium text-black dark:text-white">
                Edit Contract
              </h3>
            </div>
            <form
              className="flex flex-col gap-5.5 p-7"
              onSubmit={formik.handleSubmit}
            >
              <div className="w-full">
                <InputControl
                  label="Name"
                  type="text"
                  name="name"
                  placeholder="Enter your contract name"
                  value={formik.values.name}
                  customOnChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  showError={(formik.touched.name && formik.errors.name) ? true : false}
                  errorText={`${formik.errors.name}`}
                />
              </div>

              <DatePickerOne
                placeholder="Select Start Date"
                name="start_at"
                value={formik.values.start_at}
                label="Start Date"
                handleChange={handleDateChange}
                showError={(formik.touched.start_at && formik.errors.start_at) ? true : false}
                errorText={`${formik.errors.start_at}`}
              />

              {!formik.values.endless && (
                <DatePickerOne
                  placeholder="select End Date"
                  name="end_at"
                  value={formik.values.end_at ?? ''}
                  label="End Date"
                  handleChange={handleDateChange}
                  showError={(formik.touched.end_at && formik.errors.end_at) ? true : false}
                  errorText={`${formik.errors.end_at}`}
                />
              )}

              <SwitcherOne
                name="endless"
                label="Endless"
                enabled={formik.values.endless}
                handleChange={handleSwitcherChange}
              />

              <div className="w-full">
                <SelectGroupOne
                  label="Client"
                  handleChange={handleSelect}
                  options={options}
                  selectedOption={formik.values.locationId ?? ''}
                  name="locationId"
                />
              </div>

              <div className="c-input-control__input-wrapper -mt-8">
                <p className="py-2.5 font-medium text-black dark:text-white">Temperature Units</p>
                <Switcher
                  options={{
                    left: CycleMeasurementSymbol.Celsius,
                    right: CycleMeasurementSymbol.Fahrenheit,
                  }}
                  isEnable={formik.values.isCelsius}
                  onChange={onUnitsChange}
                  switcherId="edit-contract-units-id"
                />
              </div>

              <div className="flex justify-end gap-4.5">
                <InteractiveButton
                  text="Save"
                  submit isLoading={formik.isSubmitting}
                  disabled={disabledSubmit}
                />
              </div>
            </form>
          </div>
          {contract?.default_meta_tags && <DefaultMetaFieldsWrapper defaultMetaTags={contract?.default_meta_tags} />}
        </div>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  contract: state.contracts.contract,
  locations: state.locations.locations,
  organization: state.organizations.organization,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getContract: dispatch.contracts.getContract,
  updateContract: dispatch.contracts.updateContract,
  getLocations: dispatch.locations.getLocations,
  clearContractState: dispatch.contracts.resetState,
  getOrganization: dispatch.organizations.getOrganization,
});

export default connect(mapState, mapDispatch)(EditContract);
